import React, { useState, useEffect, useRef } from 'react';

import { Conditional } from 'common/components/layout';
import { manageDoALogSelector } from 'common/permissions/selectors';
import lodash from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';
import Select from 'react-select';
import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import { newDoaStudyFlowAccess } from 'common/features/predicates';


import { Drawer, Box, Typography, FormControlLabel, Checkbox } from '@material-ui/core';

import { replaceTimezone, toISOLocalWithTz, getDateNoTime } from 'common/util/dateTime';

import { customStyles } from 'components/common/Dropdown/DropDownListHelper';
import MultiSelectCheckbox from 'components/common/MultiSelectCheckbox/MultiSelectCheckbox';
import MultiSelect from 'components/common/MultiSelect/MultiSelect';
import {
    delegationsList,
    getDelegationAvailableUserOptions,
    getDelegationTaskOptions,
    getDelegationRoleOptions,
} from 'pages/delegationOfAuthority/selectors';
import {
    isPITenureOverlappingWithDelegation,
    isDelegationTheSameWithDefault,
    shouldUserInDropDownSignature,
    isActivePI
} from 'pages/delegationOfAuthority/util/predicates';
import { updateStudyDelegationV2, stackAnErrorMessage } from 'pages/delegationOfAuthority/v2/actions/studyDelegationsV2';
import DelegationStatement from 'pages/delegationOfAuthority/v2/components/DelegationStatement/DelegationStatement';
import { manageStudyDelegationPredicate } from 'common/permissions/studyPredicates';

import AuditHistory from '../AuditHistory/AuditHistory';
import {
    Header,
    InputSection,
    LoadingOverlay,
    FooterSidePanel,
    DOAInformationGroup,
    CreateDelegationForm,
    TaskSelectValueIndicator,
    SignatureSelectedIndicator,
    SignatureSelectValueIndicator,
} from '../index';
import styles from './EditDelegationPanel.scss';
import { binderInstitutionIdSelector } from 'common/selectors';

const SELECT_STYLES = lodash.merge({}, customStyles, {
    control: (provided, { isDisabled }) => ({
        ...provided,
        backgroundColor: isDisabled ? '#eeeeee' : 'white',
        cursor: isDisabled ? 'not-allowed' : 'default',
        borderColor: '#cccccc',
        ':hover': {
            borderColor: '#cccccc',
        },
    }),
    container: (provided, { isDisabled }) => ({
        position: 'relative',
        cursor: isDisabled ? 'not-allowed' : 'default',
    }),
    singleValue: (provided, { isDisabled }) => ({
        ...provided,
        color: isDisabled ? '#55555580' : '#0056ad',
    }),
    placeholder: (provided, { isDisabled }) => ({
        ...provided,
        color: isDisabled ? '#55555580' : '#0056ad',
    }),
});

const parseDateFormat = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }
    const dt = moment(date).format('LL');
    return moment.utc(dt);
};

const PI_ROLE = 'Principal Investigator';
const PI_LABEL = 'P.I.';
const STAFF_LABEL = 'Staff';

function EditDelegationPanel(props) {
    const {
        open,
        data,
        onClose,
        onDelete,
        study,
        studyTeam,
        canManageDoA,
        institutionTimezone,
    } = props;
    const [roleSelected, setRoleSelected] = useState(null);
    const [tasksSelected, setTasksSelected] = useState([]);

    const [startDateSelected, setStartDateSelected] = useState(null);
    const [endDateSelected, setEndDateSelected] = useState(null);

    const [currentCalendarStart, setCurrentCalendarStart] = useState(null);
    const [currentCalendarEnd, setCurrentCalendarEnd] = useState(null);

    const [suggestedSignatures, setSuggestedSignatures] = useState([]);
    const [signaturesSelected, setSignaturesSelected] = useState([]);

    const [isDirtyForm, setDirtyForm] = useState(false);
    const [auditNote, setAuditNote] = useState('');

    const [errorSections, setErrorSections] = useState([]);
    const [delegationRequiringNewPISignature, setDelegationRequiringNewPiSignature] = useState([]);

    const tenureSelectRef = useRef(null);
    const [hasChangeSig, setChangeSig] = useState(false);

    const [hasChangeEndDate, setChangeEndDate] = useState(false);

    // Binding data into fields
    useEffect(() => {
        data.startDate && setStartDateSelected(parseDateFormat(data.startDate));
        if (data.endDate) {
            setEndDateSelected(parseDateFormat(data.endDate));
        }
        const defaultRole = props.roleOptions.find((item) => item.value === data.delegationRole);
        data.delegationRole && setRoleSelected(defaultRole);

        const defaultTasks = lodash.intersectionWith(
            props.taskOptions,
            data.delegatedTasks,
            (option, data) => option.value === data.taskId
        );
        data.delegatedTasks && setTasksSelected(defaultTasks);
    }, [open]);

    useEffect(() => {
        const hasRequiredFields = !(
            lodash.isNil(roleSelected) ||
            lodash.isEmpty(tasksSelected) ||
            lodash.isNil(startDateSelected)
        );
        if (!isDirtyForm && data && hasRequiredFields) {
            setDataPendingSignature();
        }
    }, [roleSelected, startDateSelected, endDateSelected]);

    // Auto generate signature expectation when change at least 1 field
    useEffect(() => {
        if (isDirtyForm) {
            const hasRequiredFields = !(
                lodash.isNil(roleSelected) ||
                lodash.isEmpty(tasksSelected) ||
                lodash.isNil(startDateSelected)
            );

            hasRequiredFields ? suggestSignatures() : clearSuggestedSignatures();

            const existingDoAEnabled =
                lodash.get(roleSelected, 'label') === PI_ROLE && data.delegationRole !== PI_ROLE;

            existingDoAEnabled
                ? collectExistingDelegations()
                : setDelegationRequiringNewPiSignature([]);

            if (isTheSameOldData()) {
                setDirtyForm(false);
                setAuditNote('');
                setDataPendingSignature();
            }

            if (!endDateSelected) {
                setChangeEndDate(false);
            }
        }
    }, [roleSelected, tasksSelected, startDateSelected, endDateSelected]);

    useEffect(() => {
        if (signaturesSelected && data) {
            const signaturesSelectedIds = lodash.sortBy(
                signaturesSelected.map((item) => item.value)
            );
            const defaultSignatureIds = lodash.sortBy(
                data.pendingSignatures.map((item) => item.signBy)
            );

            if (lodash.isEqual(signaturesSelectedIds, defaultSignatureIds) && isTheSameOldData()) {
                setDirtyForm(false);
            }
        }
    }, [signaturesSelected]);

    useEffect(() => {
        return () => clearForm();
    }, [open]);

    const clearForm = () => {
        setRoleSelected(null);
        setTasksSelected([]);

        setStartDateSelected(null);
        setEndDateSelected(null);

        setSignaturesSelected([]);
        setSuggestedSignatures([]);

        setAuditNote('');
        setDirtyForm(false);

        setDelegationRequiringNewPiSignature([]);
        setChangeSig(false);

        setChangeEndDate(false);
    };

    const scrollIntoView = (ref) => {
        if (lodash.isNil(ref.current)) {
            return;
        }

        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Handle error when edit overlap PI - Implemented unit test on KMS-342 PR
    const markErrors = (errors, duration = 3000) => {
        setErrorSections(errors);
        setTimeout(() => setErrorSections([]), duration);
    };

    const isValidFormSubmission = () => {
        const { stackAnErrorMessage } = props;

        const errors = [];
        if (lodash.get(roleSelected, 'label') === PI_ROLE) {
            const { currentDOA } = props;
            const allOverlappingPIs = lodash.filter(
                currentDOA,
                ({ role, startDate, endDate, user }) =>
                    lodash.get(role, 'name', '') === PI_ROLE &&
                    user.id !== data.userId &&
                    isPITenureOverlappingWithDelegation(
                        [startDate, endDate],
                        [startDateSelected, endDateSelected]
                    )
            );

            if (!lodash.isEmpty(allOverlappingPIs)) {
                errors.push('StartDate', 'EndDate');
            }
        }

        if (lodash.isEmpty(errors)) {
            return true;
        }

        stackAnErrorMessage(
            'This PI can\'t be added since their tenure conflicts with an existing PI\'s tenure. ' +
            'If you want to add this PI, their tenure will need to be modified.'
        );
        scrollIntoView(tenureSelectRef);
        markErrors(errors);
        return false;
    };

    const onStartDateChange = (selected) => {
        setStartDateSelected(parseDateFormat(selected));

        setCurrentCalendarEnd(selected);
        !isDirtyForm && setDirtyForm(true);
    };

    const onEndDateChange = (selected) => {
        setEndDateSelected(parseDateFormat(selected));

        setCurrentCalendarStart(selected);
        setChangeEndDate(true);
        !isDirtyForm && setDirtyForm(true);
    };

    const onToggleNewPiSignatureForExistingDelegation = (targetDelegationId) => {
        const targetDelegation = lodash.find(
            delegationRequiringNewPISignature,
            ({ id }) => targetDelegationId === id
        );
        targetDelegation.checked = !targetDelegation.checked;
        setDelegationRequiringNewPiSignature(lodash.cloneDeep(delegationRequiringNewPISignature));
    };

    const onRemoveSignatureIndicator = (removed) => {
        const listAfterRemoved = signaturesSelected.filter(
            (signature) => removed !== signature.value
        );
        setSignaturesSelected(listAfterRemoved);

        const pendingSigIds = data.pendingSignatures.map((item) => item.signBy).sort();
        const listAfterRemovedIds = listAfterRemoved.map((item) => item.value).sort();

        if (!lodash.isEqual(pendingSigIds, listAfterRemovedIds)) {
            setChangeSig(true);
        } else {
            setChangeSig(false);
        }
    };

    const collectPISignatures = () => {
        if (lodash.get(roleSelected, 'label') === PI_ROLE) {
            return [
                {
                    label: data.delegationName,
                    value: data.userId,
                    data: { userId: data.userId, role: PI_LABEL },
                },
            ];
        }

        const { currentDOA } = props;
        return lodash
            .chain(currentDOA)
            .filter(
                ({ role, startDate, endDate, user }) =>
                    lodash.get(role, 'name', '') === PI_ROLE &&
                    user.id !== data.userId && isActivePI([startDate, endDate], parseDateFormat(moment()))
            )
            .map(({ user }) => ({
                label: user.displayName,
                value: user.id,
                data: {
                    role: PI_LABEL,
                    userId: user.id,
                },
            }))
            .uniqBy('value')
            .value();
    };

    const collectStaffSignatures = () => {
        const { doaMode } = props;
        if (doaMode === 'CENTRAL' || lodash.get(roleSelected, 'label') === PI_ROLE) {
            return [];
        }

        return [
            {
                label: data.delegationName,
                value: data.userId,
                data: { userId: data.userId, role: STAFF_LABEL },
            },
        ];
    };

    const setDataPendingSignature = () => {
        const { currentDOA } = props;
        const PIUsers = currentDOA
            .filter((item) => lodash.get(item, 'role.name', '') === PI_ROLE)
            .map((item) => item.user);

        const signatures = data.pendingSignatures.reduce((total, cur) => {
            if (cur.signatureType.includes('PI_START') && !PIUsers.length) {
                return [...total];
            }
            if (cur.signatureType.includes('STAFF_START') && props.doaMode === 'CENTRAL') {
                return [...total];
            }

            let piUser = PIUsers.filter((item) => item.id === cur.signBy)[0];
            let [label, role, userId] = ['', '', ''];

            if (cur.signatureType.includes('PI_START') && !lodash.isUndefined(piUser)) {
                [label, role, userId] = [piUser.displayName, PI_LABEL, piUser.id];
            } else {
                [label, role, userId] = [data.delegationName, STAFF_LABEL, data.userId];
            }

            return [
                ...total,
                {
                    label,
                    value: userId,
                    data: { userId, role },
                },
            ];
        }, []);
        setSignaturesSelected(signatures);

        const hasRequiredFields = !(
            lodash.isNil(roleSelected) ||
            lodash.isEmpty(tasksSelected) ||
            lodash.isNil(startDateSelected)
        );

        if (hasRequiredFields) {
            const piSignatureSuggested = collectPISignatures();
            const staffSignatureSuggested = collectStaffSignatures();
            const allSignatureSuggested = [...piSignatureSuggested, ...staffSignatureSuggested];
            const userIDsInDropdown = shouldUserInDropDownSignature(
                allSignatureSuggested,
                data.delegationSignatures
            );

            const signatureSuggested = allSignatureSuggested.filter((item) =>
                lodash.isEmpty(data.delegationSignatures)
                    ? true
                    : userIDsInDropdown.includes(item.value)
            );

            setSuggestedSignatures(signatureSuggested);
        }
    };

    const isDisableSubmit = () => {
        if ((hasChangeSig || auditNote.length) && !isDirtyForm) return false;
        if (!isDirtyForm) return true;

        return !lodash.isEmpty(data.completeSignatures) && !auditNote.length;
    };

    const isDisableEdit = () => {
        return !canManageDoA;
    };

    const renderNewPISignatureForExistingDelegation = () => {
        if (!delegationRequiringNewPISignature.length || isTheSameOldData()) return null;

        const numberOfSelected = lodash.filter(
            delegationRequiringNewPISignature,
            ({ checked }) => checked
        ).length;
        const singularOrPlural = numberOfSelected === 1 ? '' : 's';

        return (
            <Conditional condition={lodash.get(delegationRequiringNewPISignature, 'length') !== 0}>
                <section className={styles.DOASignatureExpectation} style={{ marginTop: 30 }}>
                    <Box marginTop={4} marginBottom={2}>
                        <Typography variant="body1">
                            Delegations that Require New P.I. Signature
                        </Typography>
                    </Box>

                    <DelegationStatement type="warning" size="small">
                        <Typography variant="body2">
                            Signature requests are automatically generated for any delegations that
                            will be under the tenure of the new PI. You can deselect delegations
                            that don’t require a new P.I. signature.
                        </Typography>
                    </DelegationStatement>
                    <InputSection
                        label={`${numberOfSelected} Delegation${singularOrPlural} Selected`}
                    >
                        {lodash.map(
                            delegationRequiringNewPISignature,
                            ({ id, label, checked }, index) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disableRipple
                                            color="primary"
                                            checked={checked}
                                            onChange={() =>
                                                onToggleNewPiSignatureForExistingDelegation(id)
                                            }
                                        />
                                    }
                                    label={label}
                                    key={index}
                                />
                            )
                        )}
                    </InputSection>
                </section>
            </Conditional>
        );
    };

    const collectExistingDelegations = () => {
        const { currentDOA } = props;

        const filterStaffNotFullFields = lodash.filter(
            currentDOA,
            ({ role, startDate, user, tasks }) =>
                !(
                    lodash.isEmpty(role) ||
                    lodash.isEmpty(tasks) ||
                    lodash.isEmpty(user) ||
                    lodash.isNil(startDate)
                )
        );

        const staffDelegationsOverlappingWithNewPITenure = lodash.filter(
            filterStaffNotFullFields,
            ({ role, startDate, endDate, user }) =>
                role &&
                role.name !== PI_ROLE &&
                user.id !== data.userId &&
                isPITenureOverlappingWithDelegation(
                    [startDateSelected, endDateSelected],
                    [startDate, endDate]
                )
        );

        const delegationOptions = lodash.map(
            staffDelegationsOverlappingWithNewPITenure,
            (delegation) => ({
                id: lodash.get(delegation, 'id'),
                checked: true,
                label: lodash.get(delegation, 'user.displayName'),
                data: delegation,
            })
        );

        setDelegationRequiringNewPiSignature(delegationOptions);
    };

    const suggestSignatures = () => {
        if (!isDirtyForm) return;

        const signatures = [];
        const piSignatureSuggested = collectPISignatures();
        const staffSignatureSuggested = collectStaffSignatures();

        signatures.push(...piSignatureSuggested);
        signatures.push(...staffSignatureSuggested);

        setSignaturesSelected(signatures);
        setSuggestedSignatures(signatures);
    };

    const clearSuggestedSignatures = () => {
        setSuggestedSignatures([]);
        setSignaturesSelected([]);
    };

    const isTheSameOldData = () => {
        if (lodash.isEmpty(data)) return null;

        const newDelegation = {
            ...lodash.cloneDeep(data),
            ...{
                delegatedTasks: lodash.sortBy(tasksSelected.map((item) => item.task.id)),
                delegationRole: lodash.get(roleSelected, 'label', ''),
                startDate: moment(startDateSelected).format('MM/DD/YYYY'),
                endDate: moment(endDateSelected).format('MM/DD/YYYY'),
            },
        };

        const defaultDelegation = {
            ...lodash.cloneDeep(data),
            delegatedTasks: lodash.sortBy(data.delegatedTasks.map((item) => item.taskId)),
            startDate: moment(data.rawStartDate).format('MM/DD/YYYY'),
            endDate: moment(data.rawEndDate).format('MM/DD/YYYY'),
        };

        return isDelegationTheSameWithDefault(defaultDelegation, newDelegation);
    };


    /**
     * From CRI-1579, attempt to replicate what happens when the role is changed while editing a delegation.  The
     * old code didn't update the eregulatory database.  The "delegation page" code is essentially a copy of edits
     * on the Team Members page - including doing extra updates in the `onClose` function - so I've tried to replicate
     * that same, exact pattern here for the next person.
     * 
     * @returns 
     */
    const doCloseAndCleanup = async () => {
        const userId = data.userId;
        const studyRoleId = roleSelected.role.id;
        const displayName = data.delegationName;
        const studyRoleName = roleSelected.role.name;

        const studyUserObjs = studyTeam.filter(member => member.user.id == userId);
        const studyUserId = (studyUserObjs && studyUserObjs.length > 0) ? studyUserObjs[0].id : null;
        const studyUserType = (studyUserObjs && studyUserObjs.length > 0) ? studyUserObjs[0].studyUserType : null;
        const nameOfResearcher = (studyUserObjs && studyUserObjs.length > 0) ? studyUserObjs[0].nameOfResearcher : null;
        const hasAdminRole = (studyUserObjs && studyUserObjs.length > 0) ? studyUserObjs[0].hasAdminRole : false;

        if (studyUserId) {
            props.onEdit(
                {
                    id: studyUserId,
                    userId: userId,
                    studyRoleId: studyRoleId,
                    hasAdminRole: hasAdminRole,
                    nameOfResearcher: nameOfResearcher,
                    displayName: displayName,
                    studyRoleName: studyRoleName,
                },
                studyUserType,
                studyRoleId
            );
        }


        return onClose();
    };

    const onSubmit = () => {
        if (!isValidFormSubmission()) {
            return;
        }

        const signatureExpectations = [];
        const { binderId, updateStudyDelegationV2 } = props;

        const isStillStaff =
            lodash.get(data, 'delegationRole', '') !== PI_ROLE &&
            lodash.get(roleSelected, 'value', '') !== PI_ROLE;
        const isChangePIToStaff =
            lodash.get(data, 'delegationRole', '') === PI_ROLE &&
            lodash.get(roleSelected, 'value', '') !== PI_ROLE;

        const delegation = lodash.cloneDeep({
            binderId,
            id: data.id,
            studyId: study.id,
            user: {
                id: data.userId,
                name: data.delegationName,
            },
            roleManagementId: lodash.get(roleSelected, 'role.id', null),
            tasks: lodash.map(tasksSelected, (option) => lodash.get(option, 'task')),
            startDate: moment(startDateSelected).isValid()
                ? moment(startDateSelected).toISOString()
                : null,
            endDate: moment(endDateSelected).isValid()
                ? moment(endDateSelected).toISOString()
                : null,
            notes: auditNote,
            isRevokedPIRole: isTheSameOldData() || !isChangePIToStaff,
            isStillStaff,
            auditOrigin: 'Delegation',
        });

        if (!lodash.isEmpty(signaturesSelected)) {
            const expectations = lodash.map(signaturesSelected, (option) => ({
                delegationId: delegation.id,
                user: {
                    id: lodash.get(option, 'data.userId'),
                    name: lodash.get(option, 'label'),
                },
                signatureType:
                    lodash.get(option, 'data.role') === 'P.I.' ? 'PI_START' : 'STAFF_START',
            }));

            signatureExpectations.push(...expectations);
        }

        if (!lodash.isEmpty(delegationRequiringNewPISignature)) {
            const signaturesForExistingDelegation = lodash
                .chain(delegationRequiringNewPISignature)
                .filter(({ checked }) => checked)
                .map((option) => ({
                    delegationId: lodash.get(option, 'data.id'),
                    user: {
                        id: data.userId,
                        name: data.delegationName,
                    },
                    signatureType: 'PI_START',
                }))
                .value();
            signatureExpectations.push(...signaturesForExistingDelegation);
        }

        const defaultSignatureExpectations = !isDirtyForm
            ? []
            : lodash.map(suggestedSignatures, (option) => ({
                delegationId: delegation.id,
                user: {
                    id: lodash.get(option, 'data.userId'),
                    name: lodash.get(option, 'label'),
                },
                signatureType: lodash.get(option, 'data.role') === 'P.I.' ? 'PI' : 'STAFF',
            }));

        updateStudyDelegationV2(
            binderId,
            delegation,
            signatureExpectations,
            defaultSignatureExpectations,
            doCloseAndCleanup,
            study.id
        );
    };

    function handleTaskSelected(listSelected) {
        const sortListSelected = lodash.sortBy(listSelected, 'value');
        const sortTasksSelected = lodash.sortBy(tasksSelected, 'value');

        if (!lodash.isEqual(sortListSelected, sortTasksSelected)) {
            setTasksSelected(listSelected);
            !isDirtyForm && setDirtyForm(true);
        }
    }
    function handleRoleSelected(selected) {
        setRoleSelected(selected);
        !isDirtyForm && setDirtyForm(true);
    }


    const renderInputGroup = () => (
        <DOAInformationGroup>
            <InputSection label="Role" span="2">
                <Select
                    name="role"
                    placeholder="Select Role"
                    options={props.roleOptions}
                    styles={SELECT_STYLES}
                    value={roleSelected}
                    onChange={handleRoleSelected}
                    isDisabled={isDisableEdit()}
                />
            </InputSection>

            <InputSection label="Tasks" span="2">
                <Conditional condition={!isDisableEdit()}>
                    <MultiSelect
                        className={styles.MultiSelectCheckbox}
                        options={props.taskOptions}
                        selectedOptions={tasksSelected}
                        onChanged={handleTaskSelected}
                        renderValueIndicator={TaskSelectValueIndicator}
                        placeholder="Select Study Tasks"
                    />
                </Conditional>
                <Conditional condition={isDisableEdit()}>
                    <Select
                        placeholder={`${tasksSelected.length} Study Task${tasksSelected.length === 1 ? '' : 's'}`}
                        styles={SELECT_STYLES}
                        isDisabled={isDisableEdit()}
                    />
                </Conditional>
            </InputSection>

            <InputSection
                label="Start Date"
                innerRef={tenureSelectRef}
                error={errorSections.includes('StartDate')}
            >
                <DateTimePicker
                    className={styles.DateTimePicker}
                    autoFocus={false}
                    time={false}
                    noTimezone={true}
                    value={startDateSelected}
                    onChange={onStartDateChange}
                    currentDate={currentCalendarStart}
                    onCurrentDateChange={(current) => setCurrentCalendarStart(current)}
                    max={getDateNoTime(endDateSelected) || getDateNoTime(new Date(2099, 11, 31))}
                    disabled={isDisableEdit()}
                />
            </InputSection>

            <InputSection label="End Date" error={errorSections.includes('EndDate')}>
                <DateTimePicker
                    className={styles.DateTimePicker}
                    autoFocus={false}
                    time={false}
                    noTimezone={true}
                    value={endDateSelected}
                    min={getDateNoTime(startDateSelected)}
                    onChange={onEndDateChange}
                    currentDate={currentCalendarEnd}
                    onCurrentDateChange={(current) => setCurrentCalendarEnd(current)}
                    disabled={isDisableEdit() || !startDateSelected}
                />
            </InputSection>
        </DOAInformationGroup>
    );

    const renderAuditNote = () => {
        if (!data || !canManageDoA) return null;

        const delegationConditions = {
            hasCompletedSignatures: data.completeSignatures.length > 0,
            noCompletedSignatures: data.completeSignatures.length === 0,
            hasEndDate: lodash.get(study, 'id', false) && hasChangeEndDate
        };

        return (
            <div className={styles.AuditNote}>
                <Conditional condition={delegationConditions.hasEndDate}>
                    <DelegationStatement type="warning" size="small">
                        <Typography variant="body2">
                            Setting an end date for this user's Delegation means that they will also
                            be automatically removed from the Study Team List on this date.
                        </Typography>
                    </DelegationStatement>
                </Conditional>

                <h5 className={styles.AuditNote__title}>
                    Audit Notes{' '}
                    <Conditional condition={data.completeSignatures.length > 0}>
                        <span className={styles.AuditNote__required}>*Required</span>
                    </Conditional>
                </h5>

                <textarea
                    className={styles.AuditNote__area}
                    placeholder={'Add notes to the audit record of this delegation...'}
                    value={auditNote}
                    rows={4}
                    onChange={(e) => {
                        setAuditNote(e.target.value);
                    }}
                ></textarea>
                <Conditional condition={delegationConditions.hasCompletedSignatures}>
                    <DelegationStatement type="warning" size="small">
                        <Typography variant="body2">
                            Modifying data after signature requires a note for the audit log. Also,
                            consider if these changes require additional signatures, based on your
                            organization's SOP.
                        </Typography>
                    </DelegationStatement>
                </Conditional>
                <Conditional condition={delegationConditions.noCompletedSignatures}>
                    <DelegationStatement type="warning" size="small">
                        <Typography variant="body2">
                            Modifying data before signature does not require a note for the audit
                            log. Also, consider if these changes require additional signatures,
                            based on your organization's SOP.
                        </Typography>
                    </DelegationStatement>
                </Conditional>

            </div>
        );
    };

    const notOnStudyTeam = () => {
        if (!canManageDoA) {
            return false;
        }

        const studyId = lodash.get(study, 'id', null);

        if (studyId === null) {
            return true;
        }

        const userId = lodash.get(data, 'userId', null);

        const teamMembership = studyTeam.find(teamMember => {
            return lodash.get(teamMember, 'user.id', null) === userId;
        });

        return !teamMembership;
    };

    const noCompletedSignatures = () => {
        if (!canManageDoA) {
            return false;
        }

        const completedSignatures = lodash.get(data, 'completeSignatures', []);

        return !(completedSignatures.length > 0);
    };

    const renderCompleteSignature = () => {
        const completedSignatures = lodash.get(data, 'completeSignatures', []);
        return (
            <div className={styles.DOACompletedSignatures}>
                <Box marginTop={4} marginBottom={2}>
                    <Typography variant="body1">Completed Signatures</Typography>
                </Box>
                <Typography variant="h5" component="div">
                    {completedSignatures.length} Completed Signatures
                </Typography>

                {completedSignatures.map((item) => {
                    const signedDate = replaceTimezone(
                        toISOLocalWithTz(
                            new Date(lodash.get(item, 'signature.createdAt')),
                            institutionTimezone
                        )
                    );

                    const roleLabel = lodash.get(item, 'signatureType').includes('PI_START')
                        ? PI_LABEL
                        : STAFF_LABEL;

                    return (
                        <Box key={item.id} className={styles.SignatureSelectedIndicator}>
                            <Box>
                                <span className={styles.SignatureSelectedIndicator__label}>
                                    {lodash.get(item, 'signature.user.displayName')}
                                </span>
                                <span className={styles.SignatureSelectedIndicator__role}>
                                    {roleLabel}
                                </span>
                            </Box>
                            <span className={styles.SignatureSelectedIndicator__signedDate}>
                                Signed {moment(signedDate).format('MMM DD, YYYY')}
                            </span>
                        </Box>
                    );
                })}
            </div>
        );
    };

    const groupSignatures = () => lodash.reduce(
        suggestedSignatures,
        (output, option) => {
            const targetRole = lodash.get(option, 'data.role', STAFF_LABEL);
            const targetGroup = lodash.find(output, (group) => group.label === targetRole);
            targetGroup.options.push(option);

            return output;
        },
        [
            { label: PI_LABEL, options: [] },
            { label: STAFF_LABEL, options: [] },
        ]
    );


    function handleOnSignatureMenuClosed(listSelected) {
        setSignaturesSelected(listSelected);

        const pendingSigIds = data.pendingSignatures
            .map((item) => item.signBy)
            .sort();
        const listSelectedIds = listSelected
            .map((item) => item.value)
            .sort();

        if (!lodash.isEqual(pendingSigIds, listSelectedIds)) {
            setChangeSig(true);
        } else {
            setChangeSig(false);
        }
    }

    const renderSignature = () => {
        if (!data) return;

        const groupedOptions = groupSignatures();

        const numberOfSignatures = lodash.get(suggestedSignatures, 'length');
        const isEditedButNoSignature = isDirtyForm && numberOfSignatures === 0;

        const usePluralForm = numberOfSignatures === 1 ? '' : 's';
        const hasRequiredFields = !(
            lodash.isNil(roleSelected) ||
            lodash.isEmpty(tasksSelected) ||
            lodash.isNil(startDateSelected)
        );

        return (
            <div className={styles.DOASignatureExpectation}>
                <div className={styles.DOASignatureRequest}>
                    <Box marginTop={4} marginBottom={2}>
                        <Typography variant="body1">Signature Requests</Typography>
                    </Box>

                    <Conditional condition={isEditedButNoSignature && hasRequiredFields}>
                        <DelegationStatement type="warning" size="small">
                            <Typography variant="body2">
                                Signature requests haven’t been recommended since this delegation
                                might not overlap with the PI’s tenure or the PI delegation has not
                                been added yet.
                            </Typography>
                        </DelegationStatement>
                    </Conditional>

                    <Conditional condition={isEditedButNoSignature && !hasRequiredFields}>
                        <InputSection
                            label={`${signaturesSelected.length} Signature Request${usePluralForm}`}
                        >
                            <MultiSelectCheckbox
                                className={styles.MultiSelectCheckbox}
                                options={groupedOptions}
                                selectedOptions={signaturesSelected}
                                onChanged={() => { }}
                                renderValueIndicator={SignatureSelectValueIndicator}
                                placeholder="Select Staff for Signatures"
                                searchBarPlaceholder="Search for Users"
                                isSelectAllRequired={false}
                            />
                        </InputSection>
                    </Conditional>

                    <Conditional condition={numberOfSignatures > 0 && !isTheSameOldData()}>
                        <DelegationStatement type="warning" size="small">
                            <Typography variant="body2">
                                {`${numberOfSignatures} signature request${usePluralForm} recommended. 
                                    You can remove or modify this to ensure signature ordering, or if you are unsure of the current delegation details.`}
                            </Typography>
                        </DelegationStatement>
                    </Conditional>

                    <Conditional condition={!isDisableEdit() && !isEditedButNoSignature}>
                        <InputSection
                            label={`${signaturesSelected.length} Signature Request${usePluralForm}`}
                        >
                            <MultiSelectCheckbox
                                className={styles.MultiSelectCheckbox}
                                options={groupedOptions}
                                selectedOptions={signaturesSelected}
                                onChanged={() => { }}
                                renderValueIndicator={SignatureSelectValueIndicator}
                                placeholder="Select Staff for Signatures"
                                searchBarPlaceholder="Search for Users"
                                isSelectAllRequired={false}
                                onMenuClose={handleOnSignatureMenuClosed}
                            />
                            <SignatureSelectedIndicator
                                signatures={signaturesSelected}
                                onClose={onRemoveSignatureIndicator}
                            />
                        </InputSection>
                    </Conditional>

                    <Conditional condition={isDisableEdit()}>
                        <Typography
                            component="div"
                            variant="h5"
                        >{`${signaturesSelected.length} Signature Request${usePluralForm}`}</Typography>

                        <Select
                            placeholder={`${signaturesSelected.length} selected`}
                            styles={SELECT_STYLES}
                            isDisabled={isDisableEdit()}
                        />
                        <SignatureSelectedIndicator signatures={signaturesSelected} />
                    </Conditional>

                    {renderCompleteSignature()}
                </div>
            </div>
        );
    };



    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            hideBackdrop
            disableEscapeKeyDown
            classes={{
                root: styles.EditDelegationPanel,
            }}
        >
            <div className={styles.Container}>
                <Header title={data.delegationName || ''} onClosePanel={onClose} />

                <CreateDelegationForm>
                    {renderInputGroup()}
                    {renderAuditNote()}
                    {renderSignature()}

                    {renderNewPISignatureForExistingDelegation()}

                    <AuditHistory
                        binderId={props.binderId}
                        delegationId={data.id}
                        delegationName={data.delegationName}
                        isStudyType={!lodash.isNil(lodash.get(study, 'id'))}
                        institutionId={props.institutionId}
                    />
                </CreateDelegationForm>

                <LoadingOverlay
                    loading={lodash.get(props, 'onUpdateDelegation')}
                    actionLabel={'Updating Delegation'}
                />

                <FooterSidePanel
                    onRequesting={lodash.get(props, 'onUpdateDelegation')}
                    onDeleteDelegation={onDelete}
                    onEditDelegation={onSubmit}
                    onClosePanel={onClose}
                    noCompletedSignatures={noCompletedSignatures}
                    notOnStudyTeam={notOnStudyTeam}
                    unableToSubmit={isDisableSubmit()}
                />
            </div>
        </Drawer>
    );
}

export default connect(
    (state, _props) => {
        const binderId = lodash.get(state, 'binder.id');
        const me = lodash.get(state, 'me', {});
        const study = lodash.get(state, 'study', {});
        const studyId = lodash.get(state, 'study.id', null);

        const staffOptions = getDelegationAvailableUserOptions(state);
        const taskOptions = getDelegationTaskOptions(state);

        const currentDOA = delegationsList(state);
        const roleOptions = getDelegationRoleOptions(state);

        const canManageDoALog = manageDoALogSelector(state);
        const canManageStudyDoA = manageStudyDelegationPredicate(state);
        const doaMode =
            lodash.get(me, 'isInstitutionCentralized') === 'true' ? 'CENTRAL' : 'STANDARD';

        const onUpdateDelegation = lodash.get(state, 'app.upsertDelegationRequesting', false);
        const institutionTimezone = lodash.get(me, 'institutionTimezone', 'Z');
        const institutionId = binderInstitutionIdSelector(state);
        const isNewDoaEnabled = newDoaStudyFlowAccess(state);

        return {
            me,
            doaMode,
            binderId,
            currentDOA,
            study,
            taskOptions,
            roleOptions,
            staffOptions,
            canManageDoA: studyId ? canManageStudyDoA : canManageDoALog,
            onUpdateDelegation,
            institutionTimezone,
            institutionId,
            isNewDoaEnabled
        };
    },
    { updateStudyDelegationV2, stackAnErrorMessage }
)(EditDelegationPanel);
